import { MANAGER_ROLES } from "../helpers/roles";
import { useRef, useState, Fragment } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button, Offcanvas, Dropdown } from "react-bootstrap";
import BirthdayReminderBar from "./BirthdayReminderBar";
import { loginPathWithReturn } from "../lib/returnAfterLogin";
import UserAvatar, { uploadUserAvatar, removeUserAvatar } from "./UserAvatar";

const Header = ({ user, setUser, loading }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showMenu, setShowMenu] = useState(false);

    const handleLogout = async () => {
        setShowMenu(false);
        await fetch("/api/auth/logout", {
            method: "POST",
            credentials: "include",
        });
        setUser(null);
        navigate(loginPathWithReturn(location.pathname + location.search));
    };

    const toggleMenu = () => setShowMenu((prev) => !prev);

    /** Один прихований input на весь хедер — щоб меню не губило його при перерендері. */
    const avatarInputRef = useRef(null);
    const [avatarBusy, setAvatarBusy] = useState(false);

    const pickAvatar = () => avatarInputRef.current?.click();

    const handleAvatarFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || !user?._id) return;
        setAvatarBusy(true);
        try {
            const updated = await uploadUserAvatar(user._id, file);
            setUser((prev) => ({ ...prev, avatarUrl: updated.avatarUrl, avatar: updated.avatar }));
        } catch (err) {
            alert(err.response?.data?.error || err.message || "Не вдалося завантажити фото");
        } finally {
            setAvatarBusy(false);
        }
    };

    const handleAvatarRemove = async () => {
        if (!user?._id || !window.confirm("Видалити фото?")) return;
        setAvatarBusy(true);
        try {
            await removeUserAvatar(user._id);
            setUser((prev) => ({ ...prev, avatarUrl: null, avatar: undefined }));
        } catch (err) {
            alert(err.response?.data?.error || "Не вдалося видалити фото");
        } finally {
            setAvatarBusy(false);
        }
    };

    const role = user?.role;
    const hasAnyRole = (...roles) => roles.includes(role);

    const isTargetolog = role === "targetolog";
    const isSmm = role === "smm";
    const isAdmin = role === "admin";
    const canSeeShowroomChecklist =
        hasAnyRole("admin", "director", ...MANAGER_ROLES) &&
        role !== "direct_manager" &&
        !["ritzlika@gmail.com", "annykameneva@gmail.com"].includes(user?.username);
    /** Без оплат / заяв на повернення (таргетолог окремо схований із графіка). */
    const hidePaymentsAndClaims = hasAnyRole("smm", "seamstress", "cutter");

    /** Перегляд прайсу: + менеджери та цех (редагування лише у admin/director/finance). */
    const hasPriceAccess = hasAnyRole("admin", "director", "finance", ...MANAGER_ROLES, "seamstress", "cutter");

    const linkClass = "nav-link text-white p-0 m-0 border-0 text-decoration-none";
    const itemClass = "nav-item m-0 p-0 border-0";

    /** Той самий патерн, що «Фінанси» / «Інше»: li + header-dropdown-menu у main-menu */
    const UserDesktopDropdown = ({ onClick }) => {
        if (!user) return null;
        const displayName = user.profile?.firstName || user.username || "користувач";
        return (
            <Dropdown as="li" className={itemClass}>
                <Dropdown.Toggle
                    variant="link"
                    className={`${linkClass} d-flex align-items-center`}
                    title={displayName}
                    aria-label={`Меню користувача ${displayName}`}
                >
                    <UserAvatar key={user.avatarUrl || "none"} user={user} size={34} />
                </Dropdown.Toggle>
                <Dropdown.Menu align="end" className="header-dropdown-menu">
                    <Dropdown.Header className="text-white">
                        <div className="fw-semibold">{displayName}</div>
                        <div className="small text-white-50">{user.username}</div>
                    </Dropdown.Header>
                    <Dropdown.Divider className="header-dropdown-divider" />
                    <Dropdown.Item
                        as="button"
                        type="button"
                        className="header-dropdown-item w-100 text-start"
                        disabled={avatarBusy}
                        onClick={pickAvatar}
                    >
                        {avatarBusy ? "Завантаження…" : user.avatarUrl ? "Змінити фото" : "Завантажити фото"}
                    </Dropdown.Item>
                    {user.avatarUrl ? (
                        <Dropdown.Item
                            as="button"
                            type="button"
                            className="header-dropdown-item w-100 text-start"
                            disabled={avatarBusy}
                            onClick={handleAvatarRemove}
                        >
                            Видалити фото
                        </Dropdown.Item>
                    ) : null}
                    <Dropdown.Item as={Link} to="/account" onClick={onClick} className="header-dropdown-item">
                        Пароль
                    </Dropdown.Item>
                    <Dropdown.Divider className="header-dropdown-divider" />
                    <Dropdown.Item
                        as="button"
                        type="button"
                        className="header-dropdown-item w-100 text-start"
                        onClick={handleLogout}
                    >
                        Вийти
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
        );
    };

    const AdminDesktopMenu = ({ onClick }) => (
        <ul className="main-menu navbar-nav gap-4 align-items-center">
            <li className={itemClass}>
                <Link to="/materials/purchases" onClick={onClick} className={linkClass}>
                    Виробництво
                </Link>
            </li>
            <li className={itemClass}>
                <Link to="/processes/planning" onClick={onClick} className={linkClass}>
                    Процеси
                </Link>
            </li>
            <li className={itemClass}>
                <Link to="/orders" onClick={onClick} className={linkClass}>
                    Замовлення
                </Link>
            </li>
            <li className={itemClass}>
                <Link to="/obig" onClick={onClick} className={linkClass}>
                    Обіг
                </Link>
            </li>
            <li className={itemClass}>
                <Link to="/reports/pr" onClick={onClick} className={linkClass}>
                    Таблиця PR+
                </Link>
            </li>

            <Dropdown as="li" className={itemClass}>
                <Dropdown.Toggle variant="link" className={linkClass}>
                    Фінанси
                </Dropdown.Toggle>
                <Dropdown.Menu className="header-dropdown-menu">
                    <Dropdown.Item as={Link} to="/obig" onClick={onClick} className="header-dropdown-item">
                        Обіг
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/transactions" onClick={onClick} className="header-dropdown-item">
                        Транзакції
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/pnl" onClick={onClick} className="header-dropdown-item">
                        P&amp;L
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/payments" onClick={onClick} className="header-dropdown-item">
                        Оплати
                    </Dropdown.Item>
                    {hasAnyRole("admin") && (
                        <Dropdown.Item as={Link} to="/salaries" onClick={onClick} className="header-dropdown-item">
                            Зарплати
                        </Dropdown.Item>
                    )}
                    <Dropdown.Item as={Link} to="/expenses" onClick={onClick} className="header-dropdown-item">
                        Списання
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/purchased_price" onClick={onClick} className="header-dropdown-item">
                        Склад грошей
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/novapay" onClick={onClick} className="header-dropdown-item">
                        NovaPay
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>

            <Dropdown as="li" className={itemClass}>
                <Dropdown.Toggle variant="link" className={linkClass}>
                    Аналітика
                </Dropdown.Toggle>
                <Dropdown.Menu className="header-dropdown-menu">
                    <Dropdown.Item as={Link} to="/charts" onClick={onClick} className="header-dropdown-item">
                        Δ Графік
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/matrix" onClick={onClick} className="header-dropdown-item">
                        Вигрузка
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/reports/summary" onClick={onClick} className="header-dropdown-item">
                        Загальні дані
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/reports/decomposition" onClick={onClick} className="header-dropdown-item">
                        Декомпозиція
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/reports/pr" onClick={onClick} className="header-dropdown-item">
                        Таблиця PR+
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/reports/op" onClick={onClick} className="header-dropdown-item">
                        Таблиця ОП
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
            <Dropdown as="li" className={itemClass}>
                <Dropdown.Toggle variant="link" className={linkClass}>
                    Продажі
                </Dropdown.Toggle>
                <Dropdown.Menu className="header-dropdown-menu">
                    <Dropdown.Item as={Link} to="/orders" onClick={onClick} className="header-dropdown-item">
                        Замовлення
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/returns" onClick={onClick} className="header-dropdown-item">
                        Повернення
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/returns-list" onClick={onClick} className="header-dropdown-item">
                        Заяви
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/leads" onClick={onClick} className="header-dropdown-item">
                        Ліди
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
            <Dropdown as="li" className={itemClass}>
                <Dropdown.Toggle variant="link" className={linkClass}>
                    Інше
                </Dropdown.Toggle>
                <Dropdown.Menu className="header-dropdown-menu">
                    <Dropdown.Item as={Link} to="/users" onClick={onClick} className="header-dropdown-item">
                        Користувачі
                    </Dropdown.Item>
                    <Dropdown.Item
                        as={Link}
                        to="/settings/product-names"
                        onClick={onClick}
                        className="header-dropdown-item"
                    >
                        Назви товарів (нормалізація)
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/schedule" onClick={onClick} className="header-dropdown-item">
                        Графік змін
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/payment-links" onClick={onClick} className="header-dropdown-item">
                        Посилання на оплату
                    </Dropdown.Item>
                    {canSeeShowroomChecklist && (
                        <Dropdown.Item as={Link} to="/showroom-checklist" onClick={onClick} className="header-dropdown-item">
                            Рейли шоурумів
                        </Dropdown.Item>
                    )}
                    {hasPriceAccess && (
                        <Dropdown.Item as={Link} to="/price" onClick={onClick} className="header-dropdown-item">
                            Прайс
                        </Dropdown.Item>
                    )}
                    <Dropdown.Item as={Link} to="/stocks" onClick={onClick} className="header-dropdown-item">
                        Склад
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/fb" onClick={onClick} className="header-dropdown-item">
                        Facebook
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/trading" onClick={onClick} className="header-dropdown-item">
                        Журнал
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
            <UserDesktopDropdown onClick={onClick} />
        </ul>
    );

    const DefaultMenu = ({ onClick, mobile = false }) => (
        <ul
            className={`main-menu navbar-nav ${
                mobile
                    ? "header-offcanvas-menu flex-column align-items-stretch gap-1 w-100"
                    : "gap-4 align-items-center"
            }`}
        >
            {hasAnyRole("admin", "director") && (
                <li className={itemClass}>
                    <Link to="/orders" onClick={onClick} className={linkClass}>
                        Замовлення
                    </Link>
                </li>
            )}

            {hasAnyRole("admin", "director") && (
                <li className={itemClass}>
                    <Link to="/returns" onClick={onClick} className={linkClass}>
                        Повернення
                    </Link>
                </li>
            )}

            {user && !isTargetolog && (
                <li className={itemClass}>
                    <Link to="/schedule" onClick={onClick} className={linkClass}>
                        Графік змін
                    </Link>
                </li>
            )}

            {user && (
                <li className={itemClass}>
                    <Link to="/payment-links" onClick={onClick} className={linkClass}>
                        Оплата
                    </Link>
                </li>
            )}

            {canSeeShowroomChecklist && (
                <li className={itemClass}>
                    <Link to="/showroom-checklist" onClick={onClick} className={linkClass}>
                        Рейли шоурумів
                    </Link>
                </li>
            )}

            {hasAnyRole("admin", "finance") && (
                <li className={itemClass}>
                    <Link to="/leads" onClick={onClick} className={linkClass}>
                        Ліди
                    </Link>
                </li>
            )}

            {hasAnyRole("admin", "finance") && (
                <li className={itemClass}>
                    <Link to="/fb" onClick={onClick} className={linkClass}>
                        Facebook
                    </Link>
                </li>
            )}

            {!isTargetolog && user && !hidePaymentsAndClaims && (
                <li className={itemClass}>
                    <Link to="/returns-list" onClick={onClick} className={linkClass}>
                        Заяви
                    </Link>
                </li>
            )}

            {hasAnyRole("admin", "finance") && (
                <li className={itemClass}>
                    <Link to="/transactions" onClick={onClick} className={linkClass}>
                        Транзакції
                    </Link>
                </li>
            )}

            {hasAnyRole("admin") && (
                <li className={itemClass}>
                    <Link to="/salaries" onClick={onClick} className={linkClass}>
                        Зарплати
                    </Link>
                </li>
            )}

            {role === "purchase_manager" && (
                <li className={itemClass}>
                    <Link to="/materials/purchases" onClick={onClick} className={linkClass}>
                        Виробництво
                    </Link>
                </li>
            )}

            {hasAnyRole("admin", "finance", "director") && (
                <>
                    <li className={itemClass}>
                        <Link to="/obig" onClick={onClick} className={linkClass}>
                            Обіг
                        </Link>
                    </li>
                    {!isSmm && role !== "finance" && (
                        <li className={itemClass}>
                            <Link to="/charts" onClick={onClick} className={linkClass}>
                                Δ Графік
                            </Link>
                        </li>
                    )}
                    <li className={itemClass}>
                        <Link to="/materials/purchases" onClick={onClick} className={linkClass}>
                            Виробництво
                        </Link>
                    </li>
                    <li className={itemClass}>
                        <Link to="/processes/planning" onClick={onClick} className={linkClass}>
                            Процеси
                        </Link>
                    </li>
                </>
            )}

            {hasAnyRole("admin", "targetolog") && (
                <li className={itemClass}>
                    <Link to="/matrix" onClick={onClick} className={linkClass}>
                        Вигрузка
                    </Link>
                </li>
            )}

            {hasAnyRole("admin") && (
                <li className={itemClass}>
                    <Link to="/expenses" onClick={onClick} className={linkClass}>
                        Списання
                    </Link>
                </li>
            )}

            {hasAnyRole("admin") && (
                <li className={itemClass}>
                    <Link to="/purchased_price" onClick={onClick} className={linkClass}>
                        Склад грошей
                    </Link>
                </li>
            )}

            {!isTargetolog && user && !hidePaymentsAndClaims && (
                <li className={itemClass}>
                    <Link to="/payments" onClick={onClick} className={linkClass}>
                        Оплати
                    </Link>
                </li>
            )}

            {user && (isSmm || !isTargetolog) && (
                <li className={itemClass}>
                    <Link to="/stocks" onClick={onClick} className={linkClass}>
                        Склад
                    </Link>
                </li>
            )}

            {user && hasPriceAccess && (
                <li className={itemClass}>
                    <Link to="/price" onClick={onClick} className={linkClass}>
                        Прайс
                    </Link>
                </li>
            )}

            {user && hasAnyRole("admin", "ROP") && !mobile && (
                <Dropdown as="li" className={`${itemClass} d-none d-lg-block`}>
                        <Dropdown.Toggle variant="link" className={linkClass}>
                            Звіти
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="header-dropdown-menu">
                            <Dropdown.Item as={Link} to="/reports/summary" onClick={onClick} className="header-dropdown-item">
                                Загальні дані
                            </Dropdown.Item>
                            <Dropdown.Item as={Link} to="/reports/decomposition" onClick={onClick} className="header-dropdown-item">
                                Декомпозиція
                            </Dropdown.Item>
                            <Dropdown.Item as={Link} to="/reports/pr" onClick={onClick} className="header-dropdown-item">
                                Таблиця PR+
                            </Dropdown.Item>
                            <Dropdown.Item as={Link} to="/reports/op" onClick={onClick} className="header-dropdown-item">
                                Таблиця ОП
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
            )}
            {!mobile && <UserDesktopDropdown onClick={onClick} />}
        </ul>
    );

    const NavLinks = ({ mobile = false }) => {
        const handleNavClick = () => setShowMenu(false);

        if (isAdmin) {
            return <AdminDesktopMenu onClick={handleNavClick} />;
        }

        return <DefaultMenu onClick={handleNavClick} mobile={mobile} />;
    };

    const AdminMobileMenu = () => {
        const handleNavClick = () => setShowMenu(false);

        return (
            <div className="d-flex flex-column gap-3 header-offcanvas-admin">
                <div>
                    <h6 className="text-white mb-2">Швидко</h6>
                    <ul className="ps-3 mb-0">
                        <li className="border-0">
                            <Link to="/orders" className="text-white" onClick={handleNavClick}>
                                Замовлення
                            </Link>
                        </li>
                        <li className="border-0">
                            <Link to="/obig" className="text-white" onClick={handleNavClick}>
                                Обіг
                            </Link>
                        </li>
                        <li className="border-0">
                            <Link to="/reports/pr" className="text-white" onClick={handleNavClick}>
                                Таблиця PR+
                            </Link>
                        </li>
                        <li className="border-0">
                            <Link to="/schedule" className="text-white" onClick={handleNavClick}>
                                Графік змін
                            </Link>
                        </li>
                        <li className="border-0"><Link to="/payment-links" className="text-white" onClick={handleNavClick}>Посилання на оплату</Link></li>
                        <li className="border-0"><Link to="/stocks" className="text-white" onClick={handleNavClick}>Склад</Link></li>
                    </ul>
                </div>

                <div>
                    <h6 className="text-white mb-2">Продажі</h6>
                    <ul className="ps-3 mb-0">
                        <li className="border-0"><Link to="/orders" className="text-white" onClick={handleNavClick}>Замовлення</Link></li>
                        <li className="border-0"><Link to="/returns" className="text-white" onClick={handleNavClick}>Повернення</Link></li>
                        <li className="border-0"><Link to="/returns-list" className="text-white" onClick={handleNavClick}>Заяви</Link></li>
                        <li className="border-0"><Link to="/leads" className="text-white" onClick={handleNavClick}>Ліди</Link></li>
                    </ul>
                </div>

                <div>
                    <h6 className="text-white mb-2">Фінанси</h6>
                    <ul className="ps-3 mb-0">
                        <li className="border-0"><Link to="/obig" className="text-white" onClick={handleNavClick}>Обіг</Link></li>
                        <li className="border-0"><Link to="/transactions" className="text-white" onClick={handleNavClick}>Транзакції</Link></li>
                        <li className="border-0"><Link to="/pnl" className="text-white" onClick={handleNavClick}>P&amp;L</Link></li>
                        <li className="border-0"><Link to="/payments" className="text-white" onClick={handleNavClick}>Оплати</Link></li>
                        {hasAnyRole("admin") && (
                            <li className="border-0">
                                <Link to="/salaries" className="text-white" onClick={handleNavClick}>
                                    Зарплати
                                </Link>
                            </li>
                        )}
                        <li className="border-0"><Link to="/expenses" className="text-white" onClick={handleNavClick}>Списання</Link></li>
                        <li className="border-0"><Link to="/purchased_price" className="text-white" onClick={handleNavClick}>Склад грошей</Link></li>
                        <li className="border-0"><Link to="/novapay" className="text-white" onClick={handleNavClick}>NovaPay</Link></li>
                    </ul>
                </div>

                <div>
                    <h6 className="text-white mb-2">Аналітика</h6>
                    <ul className="ps-3 mb-0">
                        <li className="border-0"><Link to="/charts" className="text-white" onClick={handleNavClick}>Δ Графік</Link></li>
                        <li className="border-0"><Link to="/matrix" className="text-white" onClick={handleNavClick}>Вигрузка</Link></li>
                        <li className="border-0"><Link to="/reports/summary" className="text-white" onClick={handleNavClick}>Загальні дані</Link></li>
                        <li className="border-0"><Link to="/reports/decomposition" className="text-white" onClick={handleNavClick}>Декомпозиція</Link></li>
                        <li className="border-0"><Link to="/reports/pr" className="text-white" onClick={handleNavClick}>Таблиця PR+</Link></li>
                        <li className="border-0"><Link to="/reports/op" className="text-white" onClick={handleNavClick}>Таблиця ОП</Link></li>
                    </ul>
                </div>

                <div>
                    <h6 className="text-white mb-2">Виробництво</h6>
                    <ul className="ps-3 mb-0">
                        <li className="border-0"><Link to="/materials/purchases" className="text-white" onClick={handleNavClick}>Виробництво</Link></li>
                        <li className="border-0"><Link to="/processes/planning" className="text-white" onClick={handleNavClick}>Процеси</Link></li>
                        <li className="border-0"><Link to="/stocks" className="text-white" onClick={handleNavClick}>Склад</Link></li>
                        {hasPriceAccess && (
                            <li className="border-0">
                                <Link to="/price" className="text-white" onClick={handleNavClick}>
                                    Прайс
                                </Link>
                            </li>
                        )}
                    </ul>
                </div>

                <div>
                    <h6 className="text-white mb-2">Маркетинг</h6>
                    <ul className="ps-3 mb-0">
                        <li className="border-0"><Link to="/fb" className="text-white" onClick={handleNavClick}>Facebook</Link></li>
                    </ul>
                </div>

                <div>
                    <h6 className="text-white mb-2">Інші</h6>
                    <ul className="ps-3 mb-0">
                        {hasPriceAccess && (
                            <li className="border-0">
                                <Link to="/price" className="text-white" onClick={handleNavClick}>Прайс</Link>
                            </li>
                        )}
                        <li className="border-0">
                            <Link to="/showroom-checklist" className="text-white" onClick={handleNavClick}>Рейли шоурумів</Link>
                        </li>
                        <li className="border-0">
                            <Link to="/trading" className="text-white" onClick={handleNavClick}>Журнал</Link>
                        </li>
                    </ul>
                </div>
            </div>
        );
    };

    const AuthButtons = ({ onCloseDrawer, desktop = false } = {}) => {
        if (loading) return null;

        const closeDrawer = () => onCloseDrawer?.();

        if (desktop) {
            if (!user) {
                return (
                    <div className="d-flex flex-wrap gap-2 align-items-center auth-holder">
                        <Link to="/login">
                            <Button variant="light" size="sm" className="text-black border border-black">
                                Вхід
                            </Button>
                        </Link>
                        {isAdmin && (
                            <Link to="/register">
                                <Button variant="dark" size="sm" className="text-white">
                                    Реєстрація
                                </Button>
                            </Link>
                        )}
                    </div>
                );
            }
            return null;
        }

        const displayName = user?.profile?.firstName || user?.username || "користувач";

        return (
            <div className="header-offcanvas-auth auth-holder w-100">
                {user && (
                    <button
                        type="button"
                        className="d-flex align-items-center gap-2 text-white small mb-3 bg-transparent border-0 p-0 text-start"
                        onClick={pickAvatar}
                        disabled={avatarBusy}
                        title="Змінити фото"
                    >
                        <UserAvatar key={user.avatarUrl || "none"} user={user} size={40} />
                        <span>
                            <span className="fw-semibold d-block">{displayName}</span>
                            <span className="text-white-50">
                                {avatarBusy ? "Завантаження…" : user.avatarUrl ? "Змінити фото" : "Завантажити фото"}
                            </span>
                        </span>
                    </button>
                )}

                {!user ? (
                    <div className="d-flex flex-wrap gap-2">
                        <Link to="/login" onClick={closeDrawer} className="text-decoration-none">
                            <Button variant="light" size="sm" className="text-black border border-black">
                                Вхід
                            </Button>
                        </Link>

                        {hasAnyRole("admin") && (
                            <Link to="/register" onClick={closeDrawer} className="text-decoration-none">
                                <Button variant="dark" size="sm" className="text-white">
                                    Реєстрація
                                </Button>
                            </Link>
                        )}
                    </div>
                ) : (
                    <div className="header-offcanvas-auth-actions">
                        <Link to="/account" onClick={closeDrawer} className="text-decoration-none">
                            <Button variant="outline-light" size="sm" className="w-100 text-dark">
                                Пароль
                            </Button>
                        </Link>
                        <Button
                            variant="outline-light"
                            size="sm"
                            className="w-100 text-dark"
                            onClick={handleLogout}
                        >
                            Вийти
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Fragment>
        <input ref={avatarInputRef} type="file" accept="image/*" hidden onChange={handleAvatarFile} />
        <header className="app-header navbar navbar-expand-lg bg-dark px-3 py-2">
            <div className="container-fluid d-flex justify-content-between align-items-center">
                <Link to="/" className="navbar-brand text-white">
                    LAVA STUDIO
                </Link>

                {!loading && (
                    <div className="d-none d-lg-flex align-items-center gap-4">
                        <NavLinks />
                        <AuthButtons desktop />
                    </div>
                )}

                <button className="navbar-toggler d-lg-none" type="button" onClick={toggleMenu}>
                    <span className="navbar-toggler-icon"></span>
                </button>

                <Offcanvas
                    show={showMenu}
                    onHide={toggleMenu}
                    placement="end"
                    className="bg-dark text-white header-mobile-drawer"
                >
                    <Offcanvas.Header closeButton closeVariant="white" className="header-mobile-drawer__head">
                        <Offcanvas.Title className="mb-0">Меню</Offcanvas.Title>
                    </Offcanvas.Header>

                    <Offcanvas.Body className="d-flex flex-column p-0 header-mobile-drawer__body">
                        <div className="header-mobile-drawer__scroll flex-grow-1 overflow-auto px-3 pt-2 pb-3">
                            {isAdmin ? <AdminMobileMenu /> : <NavLinks mobile />}

                            {!isAdmin && user && hasAnyRole("admin", "ROP") && (
                                <div className="mt-4">
                                    <h6 className="text-white-50 text-uppercase small mb-2">Звіти</h6>
                                    <ul className="list-unstyled mb-0 header-offcanvas-link-list">
                                        <li>
                                            <Link to="/reports/summary" className="text-white" onClick={toggleMenu}>
                                                Загальні дані
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="/reports/decomposition" className="text-white" onClick={toggleMenu}>
                                                Декомпозиція
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="/reports/pr" className="text-white" onClick={toggleMenu}>
                                                Таблиця PR+
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="/reports/op" className="text-white" onClick={toggleMenu}>
                                                Таблиця ОП
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div className="header-mobile-drawer__footer mt-auto px-3 pt-3 pb-4">
                            <AuthButtons onCloseDrawer={() => setShowMenu(false)} />
                        </div>
                    </Offcanvas.Body>
                </Offcanvas>
            </div>
        </header>
        {!loading && user ? <BirthdayReminderBar user={user} /> : null}
        </Fragment>
    );
};

export default Header;